import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import TelegramModal from './TelegramModal';

const DISMISS_KEY = 'tg_banner_dismissed_at';
const COOLDOWN_MS = 4 * 60 * 60 * 1000;

const TelegramLinkBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    try {
      const lastDismissed = Number(localStorage.getItem(DISMISS_KEY) || '0');
      if (Date.now() - lastDismissed < COOLDOWN_MS) return;
    } catch { /* ignore */ }
    setVisible(true);
  }, []);

  const handleDismiss = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setExiting(true);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setTimeout(() => setVisible(false), 250);
  }, []);

  const handleClick = useCallback(() => {
    setShowModal(true);
  }, []);

  if (!visible) return null;

  return (
    <>
      <AnimatePresence>
        {!exiting && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="w-full overflow-hidden"
          >
            <div className="max-w-7xl mx-auto px-3 sm:px-4 py-1.5 sm:py-2">
              <motion.div
                whileHover={{ scale: 1.005 }}
                transition={{ duration: 0.2 }}
                onClick={handleClick}
                className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#0e2044] to-[#152e5c] cursor-pointer shadow-lg shadow-black/10"
              >
                <div className="absolute inset-0 opacity-10 pointer-events-none">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(255,255,255,0.3),transparent_70%)]" />
                </div>

                <div className="relative z-10 flex items-center justify-between gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-2.5 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-6 h-6 sm:w-7 sm:h-7 bg-white/20 rounded-lg flex items-center justify-center shrink-0">
                      <ChatBubbleLeftRightIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-white" />
                    </div>
                    <p className="text-xs sm:text-sm font-medium text-white truncate min-w-0">
                      Get instant order alerts on Telegram —{' '}
                      <span className="font-bold underline underline-offset-2">Join our bot</span>
                    </p>
                  </div>
                  <button
                    onClick={handleDismiss}
                    className="p-1.5 sm:p-2 rounded-xl bg-white/20 text-white hover:bg-white/30 transition-all shrink-0 ml-2"
                    aria-label="Dismiss"
                  >
                    <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <TelegramModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </>
  );
};

export default TelegramLinkBanner;
